import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import LegalFooter from '@/components/LegalFooter';
import Seo from '@/components/Seo';

/**
 * Guides hub (/blog) — Phase 131.
 *
 * Posts now come from the backend (`GET /api/public/blog`) so the owner
 * can publish from the Admin Hub without a deploy. Visual language
 * mirrors Landing.jsx (navy / mid-blue / cyan / gold, font-display).
 */
export function formatPostDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
        return '';
    }
}

export default function Blog() {
    const [posts, setPosts] = useState(null);
    const [error, setError] = useState(false);

    const load = () => {
        setError(false);
        setPosts(null);
        api.get('/public/blog')
            .then((r) => setPosts(r.data?.items || []))
            .catch(() => { setError(true); setPosts([]); });
    };
    useEffect(() => { load(); }, []);

    const itemListJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: (posts || []).map((a, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://splashpad.pro/blog/${a.slug}`,
            name: a.title,
        })),
    };

    return (
        <div className="min-h-screen bg-[#f6fbff] text-[#0a1628]" data-testid="blog-listing-page">
            <Seo
                title="Swim School Guides | SplashPad"
                description="Practical guides for small swim schools and coaches: pricing lessons, registration, digital waivers, payments, hiring and paying staff, scheduling, and running multiple pools."
                canonical="https://splashpad.pro/blog"
                jsonLd={itemListJsonLd}
            />
            <header className="border-b border-[#0a1628]/10 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="font-display text-lg font-bold text-[#0a1628]" data-testid="blog-logo-link">
                        SplashPad
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="text-sm font-semibold text-[#1a3a5c] hover:text-[#0a1628]" data-testid="blog-login-link">
                            Log in
                        </Link>
                        <Button
                            asChild
                            className="h-10 rounded-xl bg-[#0a1628] px-4 text-sm font-semibold text-white hover:bg-[#0a1628]/90"
                            data-testid="blog-cta-signup"
                        >
                            <Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </header>

            <section className="border-b border-[#0a1628]/5 bg-white">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
                    <Badge className="mb-4 border-[#ffd70066] bg-[#ffd70022] text-[#1a3a5c] hover:bg-[#ffd70033]" data-testid="blog-hero-badge">
                        <BookOpen className="mr-1 h-3 w-3" /> Guides
                    </Badge>
                    <h1 className="font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl lg:text-6xl" data-testid="blog-hero-heading">
                        Guides for running a{' '}
                        <span style={{ background: 'linear-gradient(90deg,#00d4ff,#1a3a5c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            small swim school.
                        </span>
                    </h1>
                    <p className="mt-5 max-w-2xl text-base text-[#1a3a5c] sm:text-lg" data-testid="blog-hero-subtitle">
                        Pricing, registration, waivers, payments, hiring and paying coaches, scheduling, and
                        growing to a second pool — written by swim coaches who run schools themselves.
                    </p>
                </div>
            </section>

            <section className="bg-[#f6fbff]">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                    {posts === null && (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-article-grid-loading">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="rounded-2xl border border-[#0a1628]/10 bg-white p-6">
                                    <Skeleton className="h-3 w-32" />
                                    <Skeleton className="mt-4 h-6 w-full" />
                                    <Skeleton className="mt-2 h-6 w-3/4" />
                                    <Skeleton className="mt-4 h-16 w-full" />
                                </div>
                            ))}
                        </div>
                    )}
                    {posts !== null && error && (
                        <div className="rounded-2xl border border-[#0a1628]/10 bg-white p-8 text-center" data-testid="blog-error-state">
                            <p className="text-[#1a3a5c]">We couldn&apos;t load the guides right now.</p>
                            <Button onClick={load} variant="outline" className="mt-4" data-testid="blog-retry-button">
                                <RefreshCw className="mr-2 h-4 w-4" /> Try again
                            </Button>
                        </div>
                    )}
                    {posts !== null && !error && posts.length === 0 && (
                        <div className="rounded-2xl border border-[#0a1628]/10 bg-white p-8 text-center text-[#1a3a5c]" data-testid="blog-empty-state">
                            New guides are on the way. Check back soon.
                        </div>
                    )}
                    {posts !== null && posts.length > 0 && (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-article-grid">
                            {posts.map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/blog/${a.slug}`}
                                    className="group flex flex-col rounded-2xl border border-[#0a1628]/10 bg-white p-6 shadow-sm transition-[border-color,box-shadow] hover:border-[#00d4ff] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff]"
                                    data-testid={`blog-article-card-${a.slug}`}
                                >
                                    <div className="text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/60">
                                        {formatPostDate(a.published_at)} · {a.read_time_min} min read
                                    </div>
                                    <h2 className="mt-3 font-display text-xl font-bold leading-snug text-[#0a1628] group-hover:text-[#1a3a5c]">
                                        {a.title}
                                    </h2>
                                    <p className="mt-3 flex-1 text-sm text-[#1a3a5c]/80">{a.excerpt}</p>
                                    <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0a1628]">
                                        Read guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="border-t border-[#0a1628]/10 bg-white">
                <div className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-4 py-12 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <div>
                        <h2 className="font-display text-2xl font-bold text-[#0a1628]">Ready to run your swim school like a pro?</h2>
                        <p className="mt-1 text-sm text-[#1a3a5c]/80">Free 30-day trial. Just $0.25 per transaction — that&apos;s it.</p>
                    </div>
                    <Button asChild className="h-11 rounded-xl bg-[#0a1628] px-5 text-sm font-semibold text-white hover:bg-[#0a1628]/90" data-testid="blog-footer-cta">
                        <Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                </div>
            </section>
            <LegalFooter />
        </div>
    );
}
